import { motion } from 'framer-motion';
import { Star, Download, Eye, Heart, ExternalLink, ShoppingCart } from 'lucide-react';
import { MarketplaceItem } from '../../types/marketplace';

interface MarketplaceItemCardProps {
  item: MarketplaceItem;
  viewMode: 'list' | 'grid';
  onDownload: (item: MarketplaceItem) => void;
  onPreview?: (item: MarketplaceItem) => void;
  onFavorite?: (item: MarketplaceItem) => void;
  className?: string;
}

export const MarketplaceItemCard= ({
  item,
  viewMode,
  onDownload,
  onPreview,
  onFavorite,
  className = ''
}: MarketplaceItemCardProps) => {
  const formatDownloads = (count: number): string => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  const formatPrice = (price: number): string => {
    if (price === 0) return 'Free';
    return `$${price.toFixed(2)}`;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'workflow': return '🔄';
      case 'node': return '⚙️';
      case 'template': return '📋';
      default: return '📦';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'workflow': return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300';
      case 'node': return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';
      case 'template': return 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      whileHover={{ y: -4 }}
      className={`mb-2 px-1 d-flex ${viewMode === 'grid' ? 'col-3' : 'col-12'} ${className}`}
    >
      { viewMode === 'grid'
        ? <div className='card flex-fill'>
            <div className='card-body'>
              <div className='row'>
                <div className='col-auto'>
                  {/* Thumbnail */}
                  {
                    item.thumbnail !== ''
                      ? <img
                          src={item.thumbnail}
                          alt={item.title}
                          className="w-full h-48 object-cover"
                        />
                      : <></>

                  }
                </div>
                <div className='col'></div>
                <div className='col-auto'>
                  {/* Author */}
                  <div className="d-flex gap-2 mb-3">
                    {
                      item.author.avatar !== '' && (
                        <img
                          src={item.author.avatar}
                          alt={item.author.displayName}
                          className="w-6 h-6 rounded-full mr-2"
                        />
                      )
                    }
                    {item.author.isVerified && (
                      <span className="text-primary fw-bold" title="Verified Creator">
                        ✓
                      </span>
                    )}
                    <span className="">
                      {item.author.displayName}
                    </span>
                  </div>
                </div>
              </div>
              <div className='row'>
                <div className='col-auto'>
                  {/* Overlay badges */}
                  <div className="text-muted mb-0">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(item.type)}`}>
                      {getTypeIcon(item.type)} {item.type}
                    </span>
                    {item.isFeatured && (
                      <span className="px-2 py-1">
                        ⭐ Featured
                      </span>
                    )}
                  </div>
                </div>
                <div className='col'></div>
                <div className='col-auto'>
                  <div className="text-muted">
                    {new Date(item.publishedAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <div className='row'>
                <div className='col-auto'>
                  {/* Premium badge */}
                  {item.isPremium && (
                    <div className="text-muted mb-0">
                      <span className="px-2 py-1">
                        💎 Premium
                      </span>
                    </div>
                  )}
                </div>
                <div className='col'></div>
                <div className='col-auto px-0 text-muted'>
                  <Eye className="w-4 h-4" />
                  <span className='ps-1'>{item.viewCount}</span>
                </div>
                <div className='col-auto px-2 text-muted'>
                  <div className="text-muted">
                    <Download className="w-4 h-4" />
                    <span>{formatDownloads(item.downloadCount)}</span>
                  </div>
                </div>
              </div>
              <div className='row mt-4'> 
                <div className='col-auto'>
                  <h3 className="mb-0">
                    {item.title}
                  </h3>
                </div>
              </div>
              <div className='row'>
                <div className='col-auto'>
                  {/* Tags */}
                  <div className="d-flex gap-2 mb-2">
                    {item.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="text-muted"
                      >
                        #{tag}
                      </span>
                    ))}
                    {item.tags.length > 3 && (
                      <span className="text-muted">
                        +{item.tags.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
                <div className='col'></div>
                <div className='col-auto'>
                      <Star className="w-4 h-4 text-warning pe-1" />
                      <span className='pe-1'>{item.rating.toFixed(1)}</span>
                      <span>({item.ratingCount})</span>
                </div>
              </div>
              <div className='row'>
                <p className="">
                  {item.description}
                </p>
              </div>
              < div className='row text-center'>
                <div className="fw-bold">
                  {formatPrice(item.price)} per Usage
                </div>
              </div>
              <div className='row mt-3 justify-content-md-center'>
                <div className='col-auto px-1'>
                  {onPreview && (
                    <button
                      onClick={() => onPreview(item)}
                      className="d-flex gap-2 form-control"
                    >
                      <Eye className="w-4 h-4" />
                      Preview
                    </button>
                  )}
                </div>
                <div className='col-auto px-1'>
                  <button
                    onClick={() => onDownload(item)}
                    className={`d-flex gap-2 form-control`}
                  >
                    {item.price === 0 ? (
                      <>
                        <Download className="w-4 h-4" />
                        Download
                      </>
                    ) : (
                      <>
                        {item.isOwned ? <Download className="w-4 h-4" /> :  <ShoppingCart className="w-4 h-4" />}
                        {item.isOwned ? 'Download' : 'Purchase'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

        : <div className='row border-top border-2 w-100  d-flex align-items-center'>
          <div className='col-1'>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(item.type)}`}>
              {getTypeIcon(item.type)} {item.type}
            </span>
          </div>
          <div className='col-1'>
            <Star className="w-4 h-4 text-warning pe-1" />
            <span className='pe-1'>{item.rating.toFixed(1)}</span>
            <span>({item.ratingCount})</span>
          </div>
          <div className='col-1'>
            <div className="d-flex gap-2">
              <span className="">
                {item.author.displayName}
              </span>
            </div>
          </div>
          <div className='col-1'>
            <div className="text-muted">
              {new Date(item.publishedAt).toLocaleDateString()}
            </div>
          </div>
          <div className='col-2'>
            <div className='fw-bold'>
              {item.title}
            </div>
          </div>
          <div className='col'>
              {item.description}
          </div>
          <div className='col-auto'>
            <div className="" title='Price per Usage'>
              {formatPrice(item.price)}
            </div>
          </div>
          <div className='col-auto'>
            <button
              onClick={() => onDownload(item)}
              className={`btn btn-transparent`}
              title='Download'
            >
              <Download className="w-4 h-4" />
            </button>
          </div>
        </div>
      }
      
    </motion.div>
  );
};